<template>
  <div class="page-section page-section--soft">
    <div class="section-container">
    <section v-reveal class="pricing-sheet">
      <span class="section-label">Pricing</span>
      <h1 class="section-title">产品定价</h1>
      <p class="section-subtitle section-subtitle--center">
        方案按企业阶段与业务复杂度设计，可从基础能力开始，按需叠加仓库、智能功能与企业服务。
      </p>

      <div class="plan-grid">
        <article
          v-for="(plan, index) in plans"
          :key="plan.id"
          class="plan-card"
          :class="{ 'plan-card--highlight': plan.highlight }"
          v-reveal
          :style="{ transitionDelay: `${index * 60}ms` }"
        >
          <header class="plan-head" :class="{ 'plan-head--highlight': plan.highlight }">
            <p class="plan-name">{{ plan.name }}</p>
            <p class="plan-subtitle">{{ plan.subtitle }}</p>
            <span v-if="plan.badge" class="plan-badge">{{ plan.badge }}</span>
          </header>

          <div class="plan-body">
            <p class="plan-price">
              <strong>{{ plan.price }}</strong>
              <span>/年</span>
            </p>

            <a-button
              class="plan-cta"
              :type="plan.ctaType"
              :ghost="plan.ctaType === 'default'"
              block
              @click="onPlanCta(plan)"
            >
              {{ plan.ctaText }}
            </a-button>

            <ul class="plan-features">
              <li v-for="feature in plan.features" :key="feature">
                <a-icon type="check" class="feature-icon" />
                <span>{{ feature }}</span>
              </li>
            </ul>
          </div>
        </article>
      </div>

      <div class="addon-wrap">
        <article v-for="addon in addons" :key="addon.id" class="addon-item">
          <p class="addon-name">{{ addon.name }}</p>
          <p class="addon-price">{{ addon.price }}<span>/年</span></p>
        </article>
      </div>

      <div class="benefit-wrap">
        <p v-for="benefit in benefits" :key="benefit" class="benefit-item">
          <a-icon type="check" />
          <span>{{ benefit }}</span>
        </p>
      </div>

      <p class="section-note">* 增值方案不适用于基础版</p>
    </section>
    </div>
    <demo-register-modal :visible="demoModalVisible" @update:visible="demoModalVisible = $event" />
  </div>
</template>

<script>
import DemoRegisterModal from "@/apps/official/components/DemoRegisterModal.vue";

export default {
  components: {
    DemoRegisterModal
  },
  data() {
    return {
      demoModalVisible: false,
      plans: [
        {
          id: "basic",
          name: "基础版",
          subtitle: "初创海外仓首选",
          price: "¥0",
          ctaText: "免费使用",
          ctaType: "default",
          highlight: false,
          badge: "",
          features: [
            "每月 10,000 单",
            "2 个仓库/代理仓",
            "3 个员工账号",
            "仓库基础报表",
            "成本测算自动计算",
            "物流轨迹自动监控",
            "7x24 小时在线客服"
          ]
        },
        {
          id: "standard",
          name: "标准版",
          subtitle: "仓储效率更进一步",
          price: "¥28,000",
          ctaText: "预约演示",
          ctaType: "primary",
          highlight: false,
          badge: "",
          features: [
            "包含基础版能力",
            "5 个仓库",
            "不限代理仓数量",
            "不限员工账号",
            "不限订单数量",
            "业务时效预警",
            "线上&线下实施服务"
          ]
        },
        {
          id: "professional",
          name: "专业版",
          subtitle: "经营能力全面提升",
          price: "¥48,000",
          ctaText: "预约演示",
          ctaType: "primary",
          highlight: true,
          badge: "最受欢迎",
          features: [
            "包含标准版能力",
            "10 个仓库",
            "序列号管理",
            "AI 智能打包",
            "自动补货建议",
            "高级异常管理"
          ]
        },
        {
          id: "flagship",
          name: "旗舰版",
          subtitle: "卓越海外仓的最佳选择",
          price: "¥68,000",
          ctaText: "预约演示",
          ctaType: "primary",
          highlight: true,
          badge: "",
          features: [
            "包含专业版能力",
            "20 个仓库",
            "高级品销管理",
            "智能设备对接",
            "全渠道订单聚合",
            "企业级私有数据库"
          ]
        }
      ],
      addons: [
        {
          id: "seat",
          name: "增加 1 个仓库",
          price: "¥5,000"
        },
        {
          id: "ai",
          name: "AI 智能打包",
          price: "¥5,000"
        }
      ],
      benefits: [
        "免费对接打单系统",
        "免费对接代理仓",
        "免费自定义面单水印",
        "免费批量拆分导入面单"
      ]
    };
  },
  methods: {
    onPlanCta(plan) {
      if (!plan || plan.ctaText !== "预约演示") return;
      this.demoModalVisible = true;
    }
  }
};
</script>

<style scoped>
.section-subtitle--center {
  margin-left: auto;
  margin-right: auto;
  text-align: center;
  max-width: 760px;
}

.pricing-sheet {
  padding: 0;
}

.plan-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.plan-card {
  border-radius: var(--radius);
  overflow: hidden;
  border: 1px solid var(--border);
  background: var(--bg);
  box-shadow: var(--shadow);
  min-height: 640px;
  transition:
    transform 0.28s ease,
    box-shadow 0.28s ease,
    border-color 0.28s ease;
}

.plan-card:hover {
  transform: translateY(-6px);
  border-color: rgba(22, 119, 255, 0.25);
  box-shadow: 0 20px 48px rgba(22, 119, 255, 0.12);
}

.plan-head {
  position: relative;
  min-height: 96px;
  padding: 18px 14px 12px;
  background: linear-gradient(180deg, #ffffff 0%, #f7faff 100%);
  text-align: center;
  border-bottom: 1px dashed #d5e0fb;
}

.plan-head--highlight {
  background: linear-gradient(120deg, var(--primary) 0%, var(--primary-dark) 100%);
}

.plan-name {
  margin: 0;
  font-size: 31px;
  line-height: 1.1;
  color: #132867;
  font-weight: 700;
}

.plan-head--highlight .plan-name,
.plan-head--highlight .plan-subtitle {
  color: #fff;
}

.plan-subtitle {
  margin: 4px 0 0;
  color: #6e7a99;
  font-size: 12px;
}

.plan-badge {
  position: absolute;
  right: 8px;
  top: 8px;
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 11px;
  line-height: 1.2;
  color: #7a4600;
  background: linear-gradient(90deg, #ffe29f 0%, #ffc65e 100%);
  box-shadow: 0 4px 9px rgba(163, 97, 0, 0.2);
}

.plan-body {
  padding: 16px 18px 18px;
}

.plan-price {
  margin: 0;
  text-align: center;
  color: #29344f;
}

.plan-price strong {
  font-size: 35px;
  line-height: 1;
  font-weight: 700;
  letter-spacing: -0.2px;
}

.plan-price span {
  margin-left: 2px;
  font-size: 14px;
  color: #5c6683;
}

.plan-cta {
  margin-top: 12px;
  border-radius: 999px;
  height: 34px;
  font-size: 13px;
  font-weight: 600;
}

.plan-features {
  list-style: none;
  margin: 16px 0 0;
  padding: 0;
}

.plan-features li {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;
  color: #40506d;
  font-size: 13px;
  line-height: 1.5;
}

.feature-icon {
  margin-top: 3px;
  color: var(--primary);
  font-size: 12px;
}

.addon-wrap {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-top: 16px;
}

.addon-item {
  border-radius: 12px;
  border: 1px solid #d8e2ff;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  box-shadow: 0 10px 20px rgba(22, 52, 117, 0.08);
  text-align: center;
  padding: 16px;
}

.addon-name {
  margin: 0;
  color: #6a7694;
  font-size: 12px;
}

.addon-price {
  margin: 4px 0 0;
  font-size: 31px;
  font-weight: 700;
  color: #2b3654;
  line-height: 1.1;
}

.addon-price span {
  font-size: 14px;
  color: #6d7894;
  font-weight: 500;
}

.benefit-wrap {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin-top: 12px;
}

.benefit-item {
  margin: 0;
  border-radius: 10px;
  border: 1px solid #dce5ff;
  background: #fff;
  text-align: center;
  color: #4f5b77;
  font-size: 12px;
  box-shadow: 0 8px 14px rgba(23, 52, 114, 0.06);
  padding: 12px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.benefit-item .anticon {
  color: #54b35b;
  font-size: 11px;
}

.section-note {
  margin: 8px 0 0;
  text-align: right;
  color: #8c97b2;
  font-size: 11px;
}

@media (max-width: 991px) {
  .plan-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }

  .plan-card {
    min-height: 0;
  }

  .benefit-wrap {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 575px) {
  .plan-grid,
  .addon-wrap,
  .benefit-wrap {
    grid-template-columns: minmax(0, 1fr);
  }

  .plan-name {
    font-size: 27px;
  }

  .plan-price strong,
  .addon-price {
    font-size: 29px;
  }
}
</style>
